import uiI18n from "../i18n";
import { useTranslation as useUiTranslation } from "react-i18next";
import { deleteReservation } from "../Services/reservationService";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { onAuthStateChanged } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  LuCalendarDays,
  LuCheck,
  LuCircleAlert,
  LuClock3,
  LuMail,
  LuRefreshCcw,
  LuTrash2,
  LuUser,
  LuX,
} from "react-icons/lu";

import { getAllReservation } from "../Services/vacationsService";
import { auth } from "../firebase";
import Spinner from "../components/Spinner";

const STATUS = {
  PENDING: 0,
  CONFIRMED: 1,
  REJECTED: 2,
  CANCELLED: 3,
};

const AllReservations = () => {
  const { t } = useTranslation();

  const [reservations, setReservations] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [userId, setUserId] =
    useState(null);

  const [authReady, setAuthReady] =
    useState(false);

  const [processingId, setProcessingId] =
    useState(null);

  /*
  |--------------------------------------------------------------------------
  | Firebase Auth
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const unsubscribe =
      onAuthStateChanged(
        auth,
        (user) => {
          setUserId(user?.uid || null);
          setAuthReady(true);
        }
      );

    return unsubscribe;
  }, []);

  /*
  |--------------------------------------------------------------------------
  | Charger les réservations
  |--------------------------------------------------------------------------
  */

  const fetchReservations =
    useCallback(async () => {
      if (!userId) {
        setReservations([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response =
          await getAllReservation(
            userId
          );

        const normalizedData =
          Array.isArray(response)
            ? response
            : response
              ? [response]
              : [];

        setReservations(
          normalizedData
        );
      } catch (requestError) {
        console.error(
          "Erreur chargement réservations :",
          requestError
        );

        setError(
          requestError?.message ||
            t(
              "reservation.load_error",
              "Impossible de charger vos réservations."
            )
        );
      } finally {
        setLoading(false);
      }
    }, [userId, t]);

  useEffect(() => {
    if (!authReady) {
      return;
    }

    fetchReservations();
  }, [
    authReady,
    fetchReservations,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Date
  |--------------------------------------------------------------------------
  */

  const parseDate = (
    value
  ) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(
        value.getTime()
      )
        ? null
        : value;
    }

    const stringValue =
      String(value).trim();

    /*
      Format :
      25/08/2026
    */

    const frenchFormat =
      stringValue.match(
        /^(\d{1,2})\/(\d{1,2})\/(\d{4})/
      );

    if (frenchFormat) {
      const [, day, month, year] =
        frenchFormat;

      const parsedDate = new Date(
        Number(year),
        Number(month) - 1,
        Number(day)
      );

      return Number.isNaN(
        parsedDate.getTime()
      )
        ? null
        : parsedDate;
    }

    /*
      ISO :
      2026-08-25
      2026-08-25T12:00:00
    */

    const parsedDate =
      new Date(stringValue);

    return Number.isNaN(
      parsedDate.getTime()
    )
      ? null
      : parsedDate;
  };

  const formatDate = (
    value
  ) => {
    const date =
      parseDate(value);

    if (!date) {
      return "-";
    }

    return new Intl.DateTimeFormat(
      uiI18n.language,
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    ).format(date);
  };

  /*
  |--------------------------------------------------------------------------
  | Expiration
  |--------------------------------------------------------------------------
  */

  const isReservationExpired = (
    reservation
  ) => {
    const arrivalDate =
      parseDate(
        reservation?.arrival
      );

    if (!arrivalDate) {
      return false;
    }

    const today = new Date();

    today.setHours(
      0,
      0,
      0,
      0
    );

    const comparisonDate =
      new Date(arrivalDate);

    comparisonDate.setHours(
      0,
      0,
      0,
      0
    );

    return (
      comparisonDate < today
    );
  };

  /*
  |--------------------------------------------------------------------------
  | Status
  |--------------------------------------------------------------------------
  */

  const getStatusInfo = (
    reservation
  ) => {
    const expired =
      isReservationExpired(
        reservation
      );

    if (expired) {
      return {
        label: t(
          "expired",
          "Expirée"
        ),
        classes:
          "bg-slate-100 text-slate-600 border-slate-200",
        icon: LuClock3,
      };
    }

    switch (
      Number(
        reservation?.status
      )
    ) {
      case STATUS.PENDING:
        return {
          label: t(
            "pending",
            "En attente"
          ),
          classes:
            "bg-amber-50 text-amber-700 border-amber-200",
          icon: LuClock3,
        };

      case STATUS.CONFIRMED:
        return {
          label: t(
            "confirmed",
            "Confirmée"
          ),
          classes:
            "bg-green-50 text-green-700 border-green-200",
          icon: LuCheck,
        };

      case STATUS.REJECTED:
        return {
          label: t(
            "rejected",
            "Rejetée"
          ),
          classes:
            "bg-red-50 text-red-600 border-red-200",
          icon: LuX,
        };

      case STATUS.CANCELLED:
        return {
          label: t(
            "cancelled",
            "Annulée"
          ),
          classes:
            "bg-slate-100 text-slate-600 border-slate-200",
          icon: LuX,
        };

      default:
        return {
          label: "-",
          classes:
            "bg-slate-50 text-slate-500 border-slate-200",
          icon: LuCircleAlert,
        };
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Annuler / rejeter réservation
  |--------------------------------------------------------------------------
  */

  const handleCancel =
    async (reservation) => {
      const confirmed =
        window.confirm(
          t(
            "confirm",
            "Êtes-vous sûr de vouloir annuler cette réservation ?"
          )
        );

      if (!confirmed) {
        return;
      }

      const reservationId =
        reservation?.reservationID ??
        reservation?.reservationId ??
        reservation?.id;

      setProcessingId(
        reservationId
      );

      try {
        await deleteReservation(reservation);

        toast.success(
          t(
            "success",
            "Réservation annulée."
          )
        );

        await fetchReservations();
      } catch (requestError) {
        console.error(
          "Erreur annulation :",
          requestError
        );

        toast.error(
          requestError?.message ||
            t(
              "failed",
              "Impossible d'annuler la réservation."
            )
        );
      } finally {
        setProcessingId(null);
      }
    };

  /*
  |--------------------------------------------------------------------------
  | Statistiques
  |--------------------------------------------------------------------------
  */

  const stats = useMemo(() => {
    return reservations.reduce(
      (result, reservation) => {
        if (
          isReservationExpired(
            reservation
          )
        ) {
          result.expired += 1;

          return result;
        }

        switch (
          Number(
            reservation.status
          )
        ) {
          case STATUS.PENDING:
            result.pending += 1;
            break;

          case STATUS.CONFIRMED:
            result.confirmed += 1;
            break;

          case STATUS.REJECTED:
            result.rejected += 1;
            break;

          case STATUS.CANCELLED:
            result.cancelled += 1;
            break;

          default:
            break;
        }

        return result;
      },
      {
        pending: 0,
        confirmed: 0,
        rejected: 0,
        cancelled: 0,
        expired: 0,
      }
    );
  }, [reservations]);

  /*
  |--------------------------------------------------------------------------
  | Loading auth
  |--------------------------------------------------------------------------
  */

  if (!authReady) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-slate-50">
        <Spinner />
      </div>
    );
  }

  /*
  |--------------------------------------------------------------------------
  | Page
  |--------------------------------------------------------------------------
  */

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">

        {/* Header */}

        <div className="mb-8">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-green-600"> {uiI18n.t("ui.lina_immo_holidays")} </p>

          <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">

            <div>
              <h1 className="text-3xl font-black text-slate-950">
                {t(
                  "list_title",
                  "Mes réservations"
                )}
              </h1>

              <p className="mt-2 max-w-xl text-sm leading-6 text-slate-500">
                {t(
                  "reservation.description",
                  "Retrouvez et gérez toutes vos demandes de réservation."
                )}
              </p>
            </div>

            <button
              type="button"
              onClick={
                fetchReservations
              }
              disabled={loading}
              className="
                inline-flex
                h-11
                items-center
                justify-center
                gap-2
                rounded-xl
                border
                border-slate-200
                bg-white
                px-4
                text-sm
                font-bold
                text-slate-700
                transition
                hover:border-green-300
                hover:bg-green-50
                hover:text-green-700
                disabled:cursor-not-allowed
                disabled:opacity-50
              "
            >
              <LuRefreshCcw
                size={16}
                className={
                  loading
                    ? "animate-spin"
                    : ""
                }
              />

              {t(
                "refresh",
                "Actualiser"
              )}
            </button>
          </div>
        </div>

        {/* Statistiques */}

        {!loading &&
          reservations.length >
            0 && (
            <div className="mb-7 grid grid-cols-2 gap-3 sm:grid-cols-4">

              <StatCard
                label={t(
                  "pending",
                  "En attente"
                )}
                value={
                  stats.pending
                }
              />

              <StatCard
                label={t(
                  "confirmed",
                  "Confirmées"
                )}
                value={
                  stats.confirmed
                }
              />

              <StatCard
                label={t(
                  "cancelled",
                  "Annulées"
                )}
                value={
                  stats.cancelled +
                  stats.rejected
                }
              />

              <StatCard
                label={t(
                  "expired",
                  "Expirées"
                )}
                value={
                  stats.expired
                }
              />
            </div>
          )}

        {/* Erreur */}

        {error && (
          <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <LuCircleAlert
              size={19}
              className="mt-0.5 shrink-0"
            />

            <div>
              <p className="font-bold">
                {t(
                  "error",
                  "Erreur"
                )}
              </p>

              <p className="mt-1">
                {error}
              </p>
            </div>
          </div>
        )}

        {/* Chargement */}

        {loading ? (
          <div className="flex min-h-[360px] items-center justify-center">
            <Spinner />
          </div>
        ) : reservations.length >
          0 ? (
          <>
            {/* MOBILE */}

            <div className="space-y-4 md:hidden">
              {reservations.map(
                (reservation) => {
                  const reservationId =
                    reservation
                      ?.reservationID ??
                    reservation
                      ?.reservationId ??
                    reservation?.id;

                  const status =
                    getStatusInfo(
                      reservation
                    );

                  const StatusIcon =
                    status.icon;

                  const expired =
                    isReservationExpired(
                      reservation
                    );

                  const canCancel = !expired && reservation.status === STATUS.PENDING;

                  return (
                    <article
                      key={
                        reservationId
                      }
                      className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3">

                        <div className="min-w-0">
                          <p className="text-xs font-bold uppercase tracking-wide text-green-600">
                            {t(
                              "title",
                              "Logement"
                            )}
                          </p>

                          <h2 className="mt-1 line-clamp-2 text-lg font-black text-slate-900">
                            {reservation.title ||
                              "-"}
                          </h2>
                        </div>

                        <StatusBadge
                          icon={
                            StatusIcon
                          }
                          label={
                            status.label
                          }
                          classes={
                            status.classes
                          }
                        />
                      </div>

                      <div className="mt-5 space-y-3 border-t border-slate-100 pt-4">

                        <InfoRow
                          icon={LuUser}
                          label={t(
                            "contact",
                            "Contact"
                          )}
                          value={
                            reservation.contactName ||
                            "-"
                          }
                        />

                        <InfoRow
                          icon={LuMail}
                          label={t(
                            "email",
                            "E-mail"
                          )}
                          value={
                            reservation.email ||
                            "-"
                          }
                        />

                        <InfoRow
                          icon={
                            LuCalendarDays
                          }
                          label={t(
                            "booking_date",
                            "Réservation"
                          )}
                          value={formatDate(
                            reservation.dateReservation
                          )}
                        />
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-3">

                        <DateBox
                          label={t(
                            "start",
                            "Arrivée"
                          )}
                          value={formatDate(
                            reservation.arrival
                          )}
                        />

                        <DateBox
                          label={t(
                            "end",
                            "Départ"
                          )}
                          value={formatDate(
                            reservation.depart
                          )}
                        />
                      </div>

                      {canCancel && (
                        <button
                          type="button"
                          onClick={() =>
                            handleCancel(
                              reservation
                            )
                          }
                          disabled={
                            processingId ===
                            reservationId
                          }
                          className="
                            mt-5
                            inline-flex
                            h-11
                            w-full
                            items-center
                            justify-center
                            gap-2
                            rounded-xl
                            border
                            border-red-200
                            bg-red-50
                            text-sm
                            font-bold
                            text-red-600
                            transition
                            hover:bg-red-100
                            disabled:cursor-not-allowed
                            disabled:opacity-50
                          "
                        >
                          {processingId ===
                          reservationId ? (
                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-red-200 border-t-red-600" />
                          ) : (
                            <LuTrash2
                              size={
                                16
                              }
                            />
                          )}

                          {t(
                            "cancel",
                            "Annuler"
                          )}
                        </button>
                      )}
                    </article>
                  );
                }
              )}
            </div>

            {/* DESKTOP */}

            <div className="hidden overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm md:block">

              <div className="overflow-x-auto">
                <table className="min-w-full">

                  <thead className="border-b border-slate-100 bg-slate-50">
                    <tr>
                      <TableHeader>
                        {t(
                          "title",
                          "Logement"
                        )}
                      </TableHeader>

                      <TableHeader>
                        {t(
                          "contact",
                          "Contact"
                        )}
                      </TableHeader>

                      <TableHeader>
                        {t(
                          "email",
                          "E-mail"
                        )}
                      </TableHeader>

                      <TableHeader>
                        {t(
                          "booking_date",
                          "Réservation"
                        )}
                      </TableHeader>

                      <TableHeader>
                        {t(
                          "start",
                          "Arrivée"
                        )}
                      </TableHeader>

                      <TableHeader>
                        {t(
                          "end",
                          "Départ"
                        )}
                      </TableHeader>

                      <TableHeader>
                        {t(
                          "status",
                          "Statut"
                        )}
                      </TableHeader>

                      <TableHeader align="right">
                        {t(
                          "actions",
                          "Actions"
                        )}
                      </TableHeader>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {reservations.map(
                      (
                        reservation
                      ) => {
                        const reservationId =
                          reservation
                            ?.reservationID ??
                          reservation
                            ?.reservationId ??
                          reservation?.id;

                        const status =
                          getStatusInfo(
                            reservation
                          );

                        const StatusIcon =
                          status.icon;

                        const expired =
                          isReservationExpired(
                            reservation
                          );

                        const canCancel = !expired && reservation.status === STATUS.PENDING;

                        return (
                          <tr
                            key={
                              reservationId
                            }
                            className="transition hover:bg-slate-50/70"
                          >
                            <td className="max-w-[220px] px-5 py-4">
                              <p className="truncate text-sm font-black text-slate-900">
                                {reservation.title ||
                                  "-"}
                              </p>
                            </td>

                            <td className="px-5 py-4 text-sm font-medium text-slate-700">
                              {reservation.contactName ||
                                "-"}
                            </td>

                            <td className="px-5 py-4 text-sm text-slate-500">
                              {reservation.email ||
                                "-"}
                            </td>

                            <td className="whitespace-nowrap px-5 py-4 text-sm text-slate-500">
                              {formatDate(
                                reservation.dateReservation
                              )}
                            </td>

                            <td className="whitespace-nowrap px-5 py-4 text-sm font-semibold text-slate-700">
                              {formatDate(
                                reservation.arrival
                              )}
                            </td>

                            <td className="whitespace-nowrap px-5 py-4 text-sm font-semibold text-slate-700">
                              {formatDate(
                                reservation.depart
                              )}
                            </td>

                            <td className="px-5 py-4">
                              <StatusBadge
                                icon={
                                  StatusIcon
                                }
                                label={
                                  status.label
                                }
                                classes={
                                  status.classes
                                }
                              />
                            </td>

                            <td className="px-5 py-4 text-end">
                              {canCancel ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleCancel(
                                      reservation
                                    )
                                  }
                                  disabled={
                                    processingId ===
                                    reservationId
                                  }
                                  className="
                                    inline-flex
                                    h-9
                                    items-center
                                    justify-center
                                    gap-2
                                    rounded-xl
                                    border
                                    border-red-200
                                    bg-red-50
                                    px-3
                                    text-xs
                                    font-bold
                                    text-red-600
                                    transition
                                    hover:bg-red-100
                                    disabled:cursor-not-allowed
                                    disabled:opacity-50
                                  "
                                >
                                  {processingId ===
                                  reservationId ? (
                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-red-200 border-t-red-600" />
                                  ) : (
                                    <LuTrash2
                                      size={
                                        14
                                      }
                                    />
                                  )}

                                  {t(
                                    "cancel",
                                    "Annuler"
                                  )}
                                </button>
                              ) : (
                                <span className="text-xs text-slate-400">
                                  —
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          /* Aucun résultat */

          <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm">

            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-green-50 text-green-600">
              <LuCalendarDays
                size={30}
              />
            </span>

            <h2 className="mt-5 text-xl font-black text-slate-900">
              {t(
                "No_request",
                "Aucune réservation"
              )}
            </h2>

            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
              {t(
                "reservation.empty_description",
                "Vous n'avez aucune réservation à afficher pour le moment."
              )}
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

/*
|--------------------------------------------------------------------------
| Components
|--------------------------------------------------------------------------
*/

function TableHeader({
  children,
  align = "left",
}) {
  useUiTranslation();
  return (
    <th
      className={`
        whitespace-nowrap
        px-5
        py-4
        text-xs
        font-black
        uppercase
        tracking-wide
        text-slate-500
        ${
          align === "right"
            ? "text-right"
            : "text-left"
        }
      `}
    >
      {children}
    </th>
  );
}

function StatusBadge({
  icon: Icon,
  label,
  classes,
}) {
  useUiTranslation();
  return (
    <span
      className={`
        inline-flex
        items-center
        gap-1.5
        whitespace-nowrap
        rounded-full
        border
        px-2.5
        py-1
        text-xs
        font-black
        ${classes}
      `}
    >
      <Icon size={13} />
      {label}
    </span>
  );
}

function StatCard({
  label,
  value,
}) {
  useUiTranslation();
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
      <p className="text-xs font-bold text-slate-500">
        {label}
      </p>

      <p className="mt-2 text-2xl font-black text-slate-950">
        {value}
      </p>
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
}) {
  useUiTranslation();
  return (
    <div className="flex items-start gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-green-600">
        <Icon size={16} />
      </span>

      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
          {label}
        </p>

        <p className="mt-0.5 break-words text-sm font-semibold text-slate-700">
          {value}
        </p>
      </div>
    </div>
  );
}

function DateBox({
  label,
  value,
}) {
  useUiTranslation();
  return (
    <div className="rounded-2xl bg-slate-50 p-3">
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
        {label}
      </p>

      <p className="mt-1 text-sm font-black text-slate-800">
        {value}
      </p>
    </div>
  );
}

export default AllReservations;